import type React from "react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";

import type { BookableAssetDto } from "@/api/types";
import { BookableAssetCard } from "@/components/Bookings/BookableAssetCard";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { Box } from "@/design-system/Box";
import { Container } from "@/design-system/Container";
import { Inline } from "@/design-system/Inline";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { Tiles } from "@/design-system/Tiles";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { useSlug } from "@/hooks/useSlug";
import { bookableAssetMutations } from "@/queries/bookable-assets/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

interface BookableAssetsGridViewProps {
  assets: BookableAssetDto[];
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
}

export function BookableAssetsGridView({
  assets,
  hasMore,
  isLoading,
  onLoadMore,
}: BookableAssetsGridViewProps): React.ReactNode {
  const [assetBookings, setAssetBookings] = useState<number>(0);

  const sessionUser = useSessionUser();
  const navigate = useNavigate();
  const slug = useSlug();
  const { t } = useTranslation();
  const { componentProps: deleteModalProps, openDeleteModal } = useDeleteModal<string>("delete-asset-modal");

  const { mutateAsync: fetchBookingStats } = bookableAssetMutations.useFetchAssetStatistics();
  const deleteAsset = bookableAssetMutations.useDeleteAsset();

  const onOpenDeleteModal = useCallback(
    async (id: string) => {
      const { futureBookingsCount } = await fetchBookingStats(id);
      setAssetBookings(futureBookingsCount);
      openDeleteModal(id);
    },
    [fetchBookingStats, openDeleteModal],
  );

  const loaderRef = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isLoading && hasMore) {
        onLoadMore();
      }
    }, [onLoadMore, hasMore, isLoading]),
  });

  return (
    <>
      <Stack gap="md">
        <Container fullWidth>
          <Tiles columns={{ base: 1, container: { md: 2, xl: 3 } }} gap="md">
            {assets.length === 0 ? (
              <span className="text-body-emphasis">
                {sessionUser.isAdmin
                  ? t("page.bookings.list-assets.no-assets.admin")
                  : t("page.bookings.list-assets.no-assets.resident")}
              </span>
            ) : (
              assets.map((asset) => (
                <BookableAssetCard
                  data-testid="asset-card"
                  onDelete={() => onOpenDeleteModal(asset.id)}
                  onEdit={() => void navigate(routes.bookings.editAsset({ slug, id: asset.id }))}
                  key={asset.id}
                  {...{ asset }}
                />
              ))
            )}
          </Tiles>
        </Container>
        {hasMore && (
          <div className="p-space-md" ref={loaderRef}>
            <Box padding={{ y: "md" }}>
              <Inline justify="center">
                <Spinner size="md" />
              </Inline>
            </Box>
          </div>
        )}
      </Stack>
      <DeleteModal
        title={t("page.bookings.delete-asset.modal.title")}
        description={
          assetBookings === 0
            ? t("page.bookings.delete-asset.modal.description.no-bookings")
            : t("page.bookings.delete-asset.modal.description", { count: assetBookings })
        }
        onDelete={deleteAsset.mutateAsync}
        deleteBtnProps={{
          "data-testid": "modal-confirm-delete",
        }}
        {...deleteModalProps}
      />
    </>
  );
}
